import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { createMask } from '@ngneat/input-mask';
import { Address, SubNationalDivision } from 'src/app/classes/models';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { SubNationalDivisionService } from 'src/app/services/sub-national-division.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-vendor-address-edit',
  templateUrl: './vendor-address-edit.component.html',
  styleUrl: './vendor-address-edit.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VendorAddressEditComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private ToastService: ToastService,
    private SubNationalDivisionService: SubNationalDivisionService
  ) {}

  @Input() VendorId!: number;
  @Input() streetAddressForm!: FormGroup;
  @Input() mailingAddressForm!: FormGroup;
  @Input() remitToAddressForm!: FormGroup;
  @Input() streetAddress?: Address;
  @Input() mailingAddress?: Address;
  @Input() remitToAddress?: Address;
  @Input() country!: 'US' | 'CA';
  @Input() localizationInfo!: LocalizationInfo;

  states!: SubNationalDivision[];

  usZipMask = createMask('99999');
  canadianZipMask = createMask('A9A 9A9');

  applyIsRemitToToAddress(event: boolean) {
    if (event) {
      this.remitToAddressForm.reset();
      this.remitToAddressForm.patchValue({
        isStreet: false,
        isMailing: false,
        isRemitTo: false,
        country: this.country
      });
    }
    this.remitToAddressForm.patchValue({
      isRemitTo: !event,
      VendorId: this.VendorId
    });

    // If the mailing address form is active, we want to apply the remit to value to it.
    // Otherwise, apply the remit to value to the street address form, since it's also the mailing address.
    const { value } = this.mailingAddressForm.controls['isMailing'];

    this.mailingAddressForm.patchValue({
      isRemitTo: value ? event : false,
      VendorId: this.VendorId
    });
    this.streetAddressForm.patchValue({
      isRemitTo: value ? false : event,
      VendorId: this.VendorId
    });
  }

  applyIsMailingToAddress(event: boolean) {
    if (event) {
      this.mailingAddressForm.reset();
      this.mailingAddressForm.patchValue({
        isStreet: false,
        isMailing: false,
        isRemitTo: false,
        country: this.country
      });
    }
    const { value: streetAddressIsRemitTo } =
      this.streetAddressForm.controls['isRemitTo'];
    const { value: mailingAddressIsRemitTo } =
      this.mailingAddressForm.controls['isRemitTo'];

    // If the street address form contains the remit to value, and we're making the
    // mailing address form active, apply the remit to value to the mailing address form.
    this.mailingAddressForm.patchValue({
      isMailing: !event,
      isRemitTo: streetAddressIsRemitTo && !event,
      VendorId: this.VendorId
    });
    this.streetAddressForm.patchValue({
      isMailing: event,
      isRemitTo: mailingAddressIsRemitTo && event,
      VendorId: this.VendorId
    });

    if (
      !this.mailingAddressForm.value.isRemitTo &&
      !this.streetAddressForm.value.isRemitTo
    ) {
      this.remitToAddressForm.patchValue({
        isRemitTo: true,
        VendorId: this.VendorId
      });
    } else {
      this.remitToAddressForm.patchValue({ isRemitTo: false });
    }
  }

  ngOnInit(): void {
    if (!this.VendorId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
        returnError: true
      });
    }

    if (!this.streetAddressForm) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing street address form.',
        { returnError: true }
      );
    }

    if (!this.mailingAddressForm) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing mailing address form.',
        { returnError: true }
      );
    }

    if (!this.remitToAddressForm) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing remit-to address form.',
        { returnError: true }
      );
    }

    if (!this.country) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing country information.',
        { returnError: true }
      );
    }

    if (!new Set(['US', 'CA']).has(this.country)) {
      throw this.ToastService.addErrorToast('Error', 'Invalid country.', {
        returnError: true
      });
    }

    if (!this.localizationInfo) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing localization information.',
        { returnError: true }
      );
    }

    if (this.streetAddress) {
      this.streetAddressForm.patchValue(this.streetAddress);
    }

    if (this.mailingAddress && this.mailingAddress !== this.streetAddress) {
      this.mailingAddressForm.patchValue(this.mailingAddress);
    } else if (this.streetAddress) {
      this.streetAddressForm.patchValue({ isMailing: true });
      this.mailingAddressForm.patchValue({ isMailing: false });
    }

    if (
      this.remitToAddress &&
      this.remitToAddress !== this.streetAddress &&
      this.remitToAddress !== this.mailingAddress
    ) {
      this.remitToAddressForm.patchValue(this.remitToAddress);
    } else if (this.streetAddress && !this.mailingAddress) {
      this.streetAddressForm.patchValue({ isRemitTo: true });
      this.remitToAddressForm.patchValue({ isRemitTo: false });
    } else if (this.mailingAddress) {
      this.mailingAddressForm.patchValue({ isRemitTo: true });
      this.remitToAddressForm.patchValue({ isRemitTo: false });
    }

    this.mailingAddressForm.patchValue({
      country: this.country,
      VendorId: this.VendorId
    });
    this.streetAddressForm.patchValue({
      country: this.country,
      VendorId: this.VendorId
    });
    this.remitToAddressForm.patchValue({
      country: this.country,
      VendorId: this.VendorId
    });

    this.SubNationalDivisionService.getSubNationalDivisions({
      country: this.country === 'US' ? 'USA' : 'Canada'
    }).subscribe({
      next: (data: SubNationalDivision[]) => {
        this.states = data;
        this._cdr.markForCheck();
      }
    });
  }
}
